import React, { useState, useEffect } from 'react';
import { Shield, Users, UserCog, RefreshCw, Search, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';

// Role descriptions and permissions (static config)
const ROLE_CONFIG = {
  Admin: {
    description: 'Administrateur système - Accès complet',
    permissions: ['all'],
    color: 'bg-red-100 text-red-800'
  },
  HSE: {
    description: 'Responsable HSE - Gestion des autorisations',
    permissions: ['collaborateurs', 'caces', 'visites', 'demandes.validate'],
    color: 'bg-blue-100 text-blue-800'
  },
  Directeur: {
    description: 'Directeur de site - Signature des autorisations',
    permissions: ['collaborateurs.read', 'demandes.sign', 'autorisations.revoke'],
    color: 'bg-purple-100 text-purple-800'
  },
  Manager: {
    description: "Manager d'équipe - Consultation et demandes",
    permissions: ['collaborateurs.read_team', 'demandes.create'],
    color: 'bg-green-100 text-green-800'
  },
  RH: {
    description: 'Ressources Humaines - Gestion des visites médicales',
    permissions: ['collaborateurs', 'visites_medicales', 'formations'],
    color: 'bg-pink-100 text-pink-800'
  },
  Maintenance: {
    description: 'Maintenance - Consultation des autorisations signées',
    permissions: ['autorisations.read', 'materiel'],
    color: 'bg-orange-100 text-orange-800'
  },
  Collaborateur: {
    description: 'Collaborateur - Consultation personnelle',
    permissions: ['autorisations.read_own', 'formations.read_own'],
    color: 'bg-gray-100 text-gray-800'
  }
};

export default function GestionRoles() {
  const { currentUser } = useApp();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, rolesData] = await Promise.all([
        api.getUtilisateurs(),
        api.getRoles()
      ]);
      console.log('Roles received from API:', rolesData?.length, rolesData);
      setUsers(usersData);
      setRoles(rolesData);
    } catch (error) {
      console.error('Error loading roles:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.updateUtilisateur(userId, { role: newRole });
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      toast.success('Rôle mis à jour avec succès');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du rôle');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setDeleting(true);
      // Note: Authorization now handled server-side via JWT token
      await api.deleteUtilisateur(userToDelete.id);
      setUsers(prev => prev.filter(user => user.id !== userToDelete.id));
      toast.success('Utilisateur supprimé avec succès');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteUser = (user) => {
    if (user.id === currentUser?.id) {
      toast.error('Vous ne pouvez pas supprimer votre propre compte');
      return;
    }
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const filteredUsers = users.filter(user => {
    const matchesRole = selectedRoleFilter === 'all' || user.role === selectedRoleFilter;
    const matchesSearch = searchTerm === '' || 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.nom?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesRole && matchesSearch;
  });

  const getRoleStats = () => {
    return roles.map(role => ({
      role: role.libelle,
      code: role.code,
      count: users.filter(u => u.role === role.code || u.role === role.libelle).length
    }));
  };

  const getRoleColor = (roleCode) => {
    return ROLE_CONFIG[roleCode]?.color || 'bg-gray-100 text-gray-800';
  };

  const getRoleLibelle = (roleCode) => {
    const role = roles.find(r => r.code === roleCode || r.libelle === roleCode);
    return role ? role.libelle : roleCode || 'Non défini';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            Gestion des rôles
          </h1>
          <p className="text-muted-foreground mt-1">Gestion des rôles et permissions utilisateurs</p>
        </div>
        <Button variant="outline" onClick={loadData} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total utilisateurs</p>
                <p className="text-3xl font-bold text-foreground mt-1">{users.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Rôles définis</p>
                <p className="text-3xl font-bold text-foreground mt-1">{roles.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <Shield className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Administrateurs</p>
                <p className="text-3xl font-bold text-foreground mt-1">
                  {users.filter(u => u.role === 'Admin' || u.role === 'Administrateur').length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                <UserCog className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="roles">Rôles disponibles</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <CardTitle>Attribution des rôles</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Rechercher..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-48"
                    />
                  </div>
                  <Select value={selectedRoleFilter} onValueChange={setSelectedRoleFilter}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filtrer par rôle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les rôles</SelectItem>
                      {roles.map((role) => (
                        <SelectItem key={role.id} value={role.code}>
                          {role.libelle}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Aucun utilisateur trouvé</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Utilisateur</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Rôle actuel</TableHead>
                      <TableHead>Modifier le rôle</TableHead>
                      {currentUser?.role === 'Admin' && (
                        <TableHead className="text-right">Actions</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          {user.prenom} {user.nom}
                        </TableCell>
                        <TableCell className="text-gray-500">{user.email}</TableCell>
                        <TableCell>
                          <Badge className={getRoleColor(user.role)}>
                            {getRoleLibelle(user.role)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={user.role || ''}
                            onValueChange={(value) => handleRoleChange(user.id, value)}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue placeholder="Sélectionner" />
                            </SelectTrigger>
                            <SelectContent>
                              {roles.map((role) => (
                                <SelectItem key={role.id} value={role.code}>
                                  {role.libelle}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        {currentUser?.role === 'Admin' && (
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => confirmDeleteUser(user)}
                              disabled={user.id === currentUser?.id}
                              title={user.id === currentUser?.id ? "Vous ne pouvez pas supprimer votre propre compte" : "Supprimer cet utilisateur"}
                            >
                              <Trash2 className={`h-4 w-4 ${user.id === currentUser?.id ? 'text-gray-300' : 'text-red-500 hover:text-red-700'}`} />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="roles">
          <Card>
            <CardHeader>
              <CardTitle>Rôles et permissions</CardTitle>
              <CardDescription>Liste des rôles disponibles dans le système</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {roles.map((role) => {
                  const config = ROLE_CONFIG[role.code] || ROLE_CONFIG[role.libelle] || {};
                  const userCount = users.filter(u => u.role === role.code || u.role === role.libelle).length;
                  
                  return (
                    <div
                      key={role.id}
                      className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.color || 'bg-gray-100'}`}>
                          <Shield className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{role.libelle}</h3>
                          <p className="text-sm text-muted-foreground">
                            {config.description || `Rôle ${role.code}`}
                          </p>
                          {config.permissions && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {config.permissions.slice(0, 4).map((perm, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {perm}
                                </Badge>
                              ))}
                              {config.permissions.length > 4 && (
                                <Badge variant="outline" className="text-xs">
                                  +{config.permissions.length - 4}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">{userCount}</p>
                        <p className="text-sm text-muted-foreground">utilisateurs</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer l&apos;utilisateur{' '}
              <span className="font-semibold">{userToDelete?.prenom} {userToDelete?.nom}</span> ({userToDelete?.email}) ?
              <br /><br />
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
